import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { MessageCircle, Phone, MapPin, Clock, CheckCircle, Wrench, Navigation } from 'lucide-react';
import ConversationView from './ConversationView';
import JobCompletionModal from './JobCompletionModal';
import { useTheme } from './ThemeContext';

// Format a scheduled_time value ("HH:MM" / "HH:MM:SS") as a friendly clock time.
function formatScheduledTime(t) {
  if (!t) return '';
  const parts = String(t).split(':');
  if (parts.length < 2) return t;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return t;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function FieldTechDailyView() {
  const { colors } = useTheme();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [messageCounts, setMessageCounts] = useState({});
  const [completingJob, setCompletingJob] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTodaysJobs();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchTodaysJobs, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTodaysJobs = async () => {
    try {
      // Get today's date as a LOCAL YYYY-MM-DD string. Using toISOString() here
      // returns the UTC date, which rolls to "tomorrow" in the evening for US
      // timezones and hid the tech's jobs. Match ExpressJobWorkflow's local date.
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      const { data, error } = await supabaseData
        .from('jobs')
        .select(`
          *,
          customer:customers(id, name, phone, email, address, city, state, zip),
          quote:quotes(quote_number, total)
        `)
        .eq('scheduled_date', todayStr)
        .order('scheduled_time', { ascending: true });

      if (error) throw error;

      setJobs(data || []);
      
      // Fetch message counts for each job
      if (data && data.length > 0) {
        const counts = {};
        for (const job of data) {
          if (job.customer?.id) {
            const { count } = await supabaseData
              .from('communications')
              .select('*', { count: 'exact', head: true })
              .eq('customer_id', job.customer.id);
            counts[job.customer.id] = count || 0;
          }
        }
        setMessageCounts(counts);
      }
    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    try {
      const { error } = await supabaseData
        .from('jobs')
        .update({ status: newStatus })
        .eq('id', jobId);

      if (error) throw error;
      
      // Refresh jobs
      fetchTodaysJobs();
    } catch (error) {

      alert('Failed to update job status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#4ecca3';
      case 'in-progress': return '#F05A28';
      case 'completed': return '#45b7d1';
      case 'cancelled': return '#666';
      default: return '#888';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return 'Ready to Start';
      case 'in-progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: `3px solid ${colors.border}`,
          borderTop: `3px solid ${colors.accent}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '1rem' : '2rem',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '2rem',
        background: `linear-gradient(135deg, ${colors.bgCard} 0%, ${colors.bgInput} 100%)`,
        padding: isMobile ? '1.5rem' : '2rem',
        borderRadius: '12px',
        border: `1px solid ${colors.border}`
      }}>
        <h1 style={{
          margin: '0 0 0.5rem 0',
          fontSize: isMobile ? '1.75rem' : '2rem',
          color: colors.text
        }}>
          📱 Today's Jobs
        </h1>
        <p style={{
          margin: 0,
          color: colors.textMuted,
          fontSize: isMobile ? '0.9rem' : '1rem'
        }}>
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </p>
        <div style={{
          marginTop: '1rem',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ color: '#4ecca3', fontSize: '0.9rem' }}>
            ● {jobs.filter(j => j.status === 'scheduled').length} Ready
          </div>
          <div style={{ color: colors.accent, fontSize: '0.9rem' }}>
            ● {jobs.filter(j => j.status === 'in-progress').length} In Progress
          </div>
          <div style={{ color: '#45b7d1', fontSize: '0.9rem' }}>
            ● {jobs.filter(j => j.status === 'completed').length} Completed
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: colors.bgCard,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`
        }}>
          <Wrench size={64} style={{ color: colors.border, marginBottom: '1rem' }} />
          <h3 style={{ margin: '0 0 0.5rem 0', color: colors.textMuted }}>
            No Jobs Scheduled Today
          </h3>
          <p style={{ margin: 0, color: colors.textMuted, fontSize: '0.9rem' }}>
            Enjoy your day off! 🎉
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: isMobile ? '1rem' : '1.5rem',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))'
        }}>
          {jobs.map(job => {
            const customer = job.customer;
            const messageCount = messageCounts[customer?.id] || 0;
            
            return (
              <div
                key={job.id}
                onClick={() => setSelectedJob(job)}
                style={{
                  background: colors.bgCard,
                  border: `2px solid ${getStatusColor(job.status)}`,
                  borderRadius: '12px',
                  padding: isMobile ? '1.25rem' : '1.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 24px ${getStatusColor(job.status)}40`;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Status Badge */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: getStatusColor(job.status),
                  color: '#fff',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}>
                  {getStatusLabel(job.status)}
                </div>

                {/* Job Number */}
                <div style={{
                  fontSize: '0.85rem',
                  color: colors.textMuted,
                  marginBottom: '0.5rem',
                  fontFamily: 'monospace'
                }}>
                  {job.job_number}
                </div>

                {/* Customer Info */}
                <h3 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.25rem',
                  color: colors.text,
                  paddingRight: '6rem'
                }}>
                  {customer?.name || 'Unknown Customer'}
                </h3>

                {/* Contact Info */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  marginBottom: '1rem'
                }}>
                  {customer?.phone && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      color: colors.textMuted
                    }}>
                      <Phone size={16} style={{ flexShrink: 0 }} />
                      <a
                        href={`tel:${customer.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          color: '#45b7d1',
                          textDecoration: 'none',
                          fontSize: '0.9rem'
                        }}
                      >
                        {customer.phone}
                      </a>
                    </div>
                  )}

                  {customer?.address && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.75rem',
                      color: colors.textMuted
                    }}>
                      <MapPin size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${customer.address}, ${customer.city}, ${customer.state} ${customer.zip}`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          color: '#45b7d1',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          lineHeight: '1.4'
                        }}
                      >
                        {customer.address}<br />
                        {customer.city}, {customer.state} {customer.zip}
                      </a>
                    </div>
                  )}

                  {job.scheduled_time && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      color: colors.textMuted
                    }}>
                      <Clock size={16} style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: '0.9rem' }}>
                        {formatScheduledTime(job.scheduled_time)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  marginTop: '1.5rem',
                  paddingTop: '1rem',
                  borderTop: `1px solid ${colors.border}`
                }}>
                  {/* Message Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedJob(job);
                    }}
                    style={{
                      flex: 1,
                      background: colors.accent,
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem',
                      position: 'relative',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#ff5520'}
                    onMouseOut={(e) => e.currentTarget.style.background = colors.accent}
                  >
                    <MessageCircle size={18} />
                    <span>Message</span>
                    {messageCount > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        background: '#4ecca3',
                        color: '#000',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        border: `2px solid ${colors.bgCard}`
                      }}>
                        {messageCount}
                      </div>
                    )}
                  </button>

                  {/* Status Update Button */}
                  {job.status === 'scheduled' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateJobStatus(job.id, 'in-progress');
                      }}
                      style={{
                        flex: 1,
                        background: colors.bgInput,
                        border: '1px solid #3a3a3a',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        color: colors.text,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = colors.bgHover;
                        e.currentTarget.style.borderColor = '#4a4a4a';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = colors.bgInput;
                        e.currentTarget.style.borderColor = colors.border;
                      }}
                    >
                      <Wrench size={18} />
                      <span>Start</span>
                    </button>
                  )}

                  {job.status === 'in-progress' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCompletingJob(job);
                      }}
                      style={{
                        flex: 1,
                        background: colors.bgInput,
                        border: '1px solid #3a3a3a',
                        borderRadius: '8px',
                        padding: '0.75rem',
                        color: colors.text,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = colors.bgHover;
                        e.currentTarget.style.borderColor = '#4a4a4a';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = colors.bgInput;
                        e.currentTarget.style.borderColor = colors.border;
                      }}
                    >
                      <CheckCircle size={18} />
                      <span>Complete</span>
                    </button>
                  )}
                </div>

                {/* Description */}
                {job.description && (
                  <div style={{
                    marginTop: '1rem',
                    padding: '0.75rem',
                    background: colors.bgCard,
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    color: colors.textSecondary,
                    lineHeight: '1.5'
                  }}>
                    {job.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Conversation Modal */}
      {selectedJob && selectedJob.customer && (
        <ConversationView
          customer={selectedJob.customer}
          job={selectedJob}
          onClose={() => {
            setSelectedJob(null);
            fetchTodaysJobs(); // Refresh to update message counts
          }}
        />
      )}

      {/* Job Completion Modal — routes completion through the full invoice +
          inventory-deduct + signature flow instead of a bare status update */}
      {completingJob && (
        <JobCompletionModal
          job={completingJob}
          companyId={completingJob.company_id}
          onClose={() => {
            setCompletingJob(null);
            fetchTodaysJobs();
          }}
          onComplete={() => {
            setCompletingJob(null);
            fetchTodaysJobs();
          }}
          onNavigate={() => {}}
        />
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
